import { ReactNode } from 'react';
import { ButtonPanel, ButtonPanelProps } from '@components';
import { useAppSelector, useEditMode } from '@hooks';
import { Box, BoxProps, Button } from '@mui/material';
import { isBlockEdit } from '@store/edit/edit.slice';
import { camel2title } from '@utils/index';

export type EditBoxProps = BoxProps & {
  property: string;
  name?: string;
  children?: ReactNode;
  contentArea?: boolean;
};

/**
 * Wrap children in EPI edit box if user is in edit mode
 */
export const BlockProperty = ({ property, children, contentArea }: EditBoxProps) => {
  const shouldShowButtons = useAppSelector(isBlockEdit);
  return shouldShowButtons ? (
    <Box component='span' data-epi-edit={property}>
      {children}
      {!children && contentArea && (
        <Box>
          Content area <i>'{property}'</i> is empty. Click to add blocks.
        </Box>
      )}
    </Box>
  ) : (
    <>{children}</>
  );
};

export const EditableContentArea = ({ children, property }: EditBoxProps) => {
  const { inEditMode } = useEditMode();
  return inEditMode ? (
    <Box component='div' data-epi-property-name={property}>
      {children}
    </Box>
  ) : (
    <>{children}</>
  );
};

/**
 * Use to create Edit Button for hard to click properties.
 * Renders a Button when content is in editable mode
 */
export const EditButton = ({ property = '' }: EditBoxProps) => {
  const shouldShowButtons = useAppSelector(isBlockEdit);
  return shouldShowButtons ? (
    <Button
      variant='secondary'
      data-epi-edit={property}
      sx={{ textTransform: 'none', fontWeight: 400, whiteSpace: 'nowrap' }}
    >
      {camel2title(property)}
    </Button>
  ) : null;
};

export type EditPanelProps = ButtonPanelProps & {
  children?: ReactNode;
  properties?: string[];
  disableEditPanel: boolean;
};

export const EditPanel = ({ children, properties, disableEditPanel, ...rest }: EditPanelProps) => {
  const { inBlockEdit, isEditable } = useEditMode();
  const shouldShowButtons = !disableEditPanel && inBlockEdit && isEditable;
  return shouldShowButtons ? (
    <ButtonPanel {...rest} sx={{ py: 2, flexWrap: 'wrap', gap: 2 }}>
      {children}
      {properties?.map((property, key) => (
        <EditButton property={property} key={key} sx={{ maxWidth: 'auto', width: 'auto' }} />
      ))}
    </ButtonPanel>
  ) : null;
};
